import filter from 'lodash.filter';

import { RequestError } from './errors';
import { CustomContentClient } from './CustomContentClient';
import { CustomStaticContentClient } from './CustomStaticContentClient';

export class ApplicationContentClient {
    constructor(httpClient) {
        this.httpClient = httpClient;
        this.customContentClient = new CustomContentClient(
            httpClient,
            this.constructor.BASE_CONTENT_URL,
            this.constructor.SECURE_CONTENT_URL,
            this.constructor.ASSESSMENT_SHORT_NAME,
        );
        this.customStaticContentClient = new CustomStaticContentClient(
            httpClient,
            this.constructor.BASE_STATIC_CONTENT_URL,
        );
    }

    /**
     * Retrieve forms satisfying search parameters.
     * @param {Object} filterParams object with forms params to filter by
     * @param {String} token - keycloak token for API authorization
     * @return {Object[]} list of forms objects
     */
    async getForms(filterParams, token) {
        let allForms = [];

        try {
            allForms = await this.customContentClient.getForms([], token);
        } catch (error) {
            throw new RequestError(
                `${this.constructor.ASSESSMENT} get forms request failed`,
                error,
                this.constructor.ASSESSMENT,
            );
        }

        const forms = filter(allForms, filterParams);

        if (forms.length === 0) {
            throw new Error(
                `Unable to find forms for${Object.keys(filterParams).map(
                    key => ` ${key}:${filterParams[key]}`,
                )}.`,
            );
        }

        return forms;
    }

    /**
     * Retrieve secure form by form uid.
     * @param {String} formUid secure form uid
     * @param {Number} year - school year
     * @param {String} token - keycloak token for API authorization
     * @return {Object[]} list of forms objects
     */
    async getSecureForm(formUid, year, token) {
        let form;

        try {
            form = await this.customContentClient.getSecureForm(formUid, year, token);
        } catch (error) {
            throw new RequestError(
                `${this.constructor.ASSESSMENT} get secure form by Uid request failed`,
                error,
                this.constructor.ASSESSMENT,
            );
        }

        return [form];
    }

    /**
     * Retrieve single form by form uid.
     * @param {String} formUid - regular form uid
     * @param {String} token - keycloak token for API authorization
     * @returns {Object[]}
     */
    async getForm(formUid, token) {
        let form;

        try {
            form = await this.customContentClient.getForm(formUid, token);
        } catch (error) {
            throw new RequestError(
                `${this.constructor.ASSESSMENT} get form by Uid request failed`,
                error,
                this.constructor.ASSESSMENT,
            );
        }

        return [form];
    }

    /**
     * Retrieve cutscores satisfying search parameters.
     * @param {String} grade
     * @param {String} period
     * @param {String} measures
     * @param {String} token - keycloak token for API authorization
     * @returns {Promise<Object>} forms
     */
    async getCutscores(grade, period, measures, token) {
        let allCutscores = [];
        try {
            allCutscores = await this.customContentClient.getCutscores([], token);
        } catch (error) {
            throw new RequestError(
                `${this.constructor.ASSESSMENT} get cutscores request failed`,
                error,
                this.constructor.ASSESSMENT,
            );
        }

        const cutscores = filter(
            allCutscores,
            cutscore =>
                cutscore.grade === grade &&
                cutscore.period === period &&
                measures.includes(cutscore.measure),
        );

        measures.forEach(measure => {
            if (!cutscores.find(cutscore => cutscore.measure === measure)) {
                // eslint-disable-next-line no-console
                console.error(
                    `No cutscore for measure ${measure}, grade ${grade}, period ${period} combination`,
                );
            }
        });

        return cutscores || [];
    }

    /**
     * @param {String} token - keycloak token for API authorization
     * @param {String} measure measure to get reminders for
     * @return {Object[]} list of reminders objects
     */
    async getReminders(measure, token) {
        let reminders = [];
        try {
            reminders = await this.customStaticContentClient.getReminders(token);
        } catch (error) {
            throw new RequestError(
                `${this.constructor.ASSESSMENT} get reminders request failed`,
                error,
                this.constructor.ASSESSMENT,
            );
        }

        const measureContent = reminders.find(item => item.measure === measure);

        if (!measureContent) {
            throw new Error(`Could not find redminders for ${measure}`);
        }

        return measureContent;
    }

    /**
     * @param {String} token - keycloak token for API authorization
     * @param {String} measure measure to get instructions for
     * @return {Object[]} list of instructions objects
     */
    async getInstructions(measure, token) {
        let instructions = [];
        try {
            instructions = await this.customStaticContentClient.getInstructions(token);
        } catch (error) {
            throw new RequestError(
                `${this.constructor.ASSESSMENT} get instructions request failed`,
                error,
                this.constructor.ASSESSMENT,
            );
        }

        const measureContent = instructions.find(item => item.measure === measure);

        if (!measureContent) {
            throw new Error(`Could not find instructions for ${measure}`);
        }

        return measureContent;
    }
}

export default ApplicationContentClient;
